import { useState, useEffect } from "react";
import { FileDown, Gauge, FileCode, LogOut, FolderOpen } from "lucide-react";
import { useMember } from "../contexts/MemberContext";
import { MemberLoginScreen } from "../components/member/MemberLoginScreen";
import { MemberPaywall } from "../components/member/MemberPaywall";
import { BidReadinessScorer } from "../components/member/BidReadinessScorer";
import GrantAutomation from "./GrantAutomation";
import { memberApi } from "../lib/memberApi";

const TOOLS = [
  { id: "capability", label: "Capability Statement", icon: FileDown },
  { id: "scorer", label: "Bid Readiness Scorer", icon: Gauge },
  { id: "writer", label: "AI Grant Writer", icon: FileCode },
  { id: "documents", label: "Saved Documents", icon: FolderOpen },
];

export default function MemberPortal() {
  const { member, loading, login, logout, subscribe, refreshSubscriptionStatus } = useMember();
  const [activeTool, setActiveTool] = useState("capability");
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [documentsRefreshKey, setDocumentsRefreshKey] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId || !member) return;

    setCheckingAccess(true);
    refreshSubscriptionStatus(sessionId)
      .then((status) => {
        if (status === "active") setActiveTool("scorer");
      })
      .finally(() => {
        window.history.replaceState({}, "", `${window.location.origin}/member`);
        setCheckingAccess(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.member_id]);

  useEffect(() => {
    if (member?.subscription_status === "active") {
      setActiveTool((current) => current || "scorer");
    }
  }, [member?.subscription_status]);

  if (loading || member === null || checkingAccess) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[var(--text-2)]" data-testid="member-portal-loading">Loading…</div>;
  }
  if (!member) {
    return <MemberLoginScreen onLogin={login} />;
  }
  if (member.subscription_status !== "active") {
    return <MemberPaywall member={member} onSubscribe={subscribe} onLogout={logout} onRefreshAccess={refreshSubscriptionStatus} />;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-14 pb-24" data-testid="member-dashboard">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="overline text-[var(--red)] mb-2">Grant Partnership</div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Welcome back, {member.name?.split(" ")[0] || "there"}</h1>
        </div>
        <button onClick={logout} className="text-xs text-[var(--text-3)] hover:text-white flex items-center gap-2" data-testid="member-dashboard-logout-btn">
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>

      <div className="flex gap-2 border-b border-[var(--border)] mb-10">
        {TOOLS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTool(t.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${
              activeTool === t.id ? "border-[var(--red)] text-white" : "border-transparent text-[var(--text-2)] hover:text-white"
            }`}
            data-testid={`member-tool-${t.id}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {activeTool === "capability" && <CapabilityStatementTool />}
      {activeTool === "scorer" && <BidReadinessScorer />}
      {activeTool === "writer" && <GrantAutomation apiClient={memberApi} memberMode onDocumentCreated={() => setDocumentsRefreshKey((k) => k + 1)} />}
      {activeTool === "documents" && <MemberDocuments key={documentsRefreshKey} />}
    </div>
  );
}

function CapabilityStatementTool() {
  const handleDownload = async () => {
    const res = await memberApi.get("/capability-statement.pdf", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "PHM-Capability-Statement.pdf";
    a.click();
  };
  return (
    <div className="card-dark p-10 max-w-lg" data-testid="capability-statement-tool">
      <FileDown className="w-8 h-8 text-[var(--red)] mb-5" />
      <h2 className="font-display text-xl font-semibold tracking-tight mb-2">Capability Statement</h2>
      <p className="text-sm text-[var(--text-2)] mb-6">Always up to date, generated on demand as a downloadable PDF.</p>
      <button onClick={handleDownload} className="btn-red" data-testid="capability-download-btn">
        Download PDF
      </button>
    </div>
  );
}

function MemberDocuments() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await memberApi.get("/member/documents");
        setDocuments(data);
      } catch (err) {
        setError("Could not load your saved documents.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const download = async (doc) => {
    const res = await memberApi.get(`/member/documents/${doc.id}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.mime_type }));
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="text-[var(--text-2)]" data-testid="member-documents-loading">Loading saved documents…</div>;
  }

  if (error) {
    return <div className="text-sm text-[var(--red)]" data-testid="member-documents-error">{error}</div>;
  }

  return (
    <div className="space-y-4" data-testid="member-documents-list">
      {documents.length === 0 ? (
        <div className="card-dark p-8 text-sm text-[var(--text-2)]" data-testid="member-documents-empty">No saved documents yet. Generate a summary or almost-ready package to see it here.</div>
      ) : (
        documents.map((doc) => (
          <div key={doc.id} className="card-dark p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid={`member-document-${doc.id}`}>
            <div>
              <div className="font-display text-lg font-semibold">{doc.filename}</div>
              <div className="text-xs text-[var(--text-3)] uppercase tracking-[0.18em] mt-1">{doc.document_type.replace(/_/g, " ")}</div>
              <div className="text-sm text-[var(--text-2)] mt-2">Created {new Date(doc.created_at).toLocaleString()}</div>
            </div>
            <button onClick={() => download(doc)} className="btn-ghost" data-testid={`member-document-download-${doc.id}`}>
              <FileDown className="w-4 h-4" /> Download
            </button>
          </div>
        ))
      )}
    </div>
  );
}
